import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

import { CatalogItemInOrder } from 'entities/CatalogItem/model/types/CatalogItemSchema';
import {
    getSelectedOrder,
    getShipmentStatus,
} from 'entities/Order/model/selectors/ordersSelectors';
import {
    FetchSupplierOrderIdsResponse,
    Order,
    ShipmentSchema,
} from 'entities/Order/model/types/OrderSchema';
import { SelectedProposal } from 'entities/Proposal/model/types/ProposalSchema';
import { getPropertyVendorsMap } from 'entities/Vendors/model/selectors/vendorsSelectors';
import { Vendor } from 'entities/Vendors/model/types/VendorsSchema';
import { $api } from 'shared/api/api';

export interface SupplierInfo {
    name: string;
    logo?: string;
    sellerUid: string;
    accountNumber?: string;
    phone?: string;
    email?: string;
    primaryContact?: string;
    primaryPhone?: string;
    primaryEmail?: string;
    shipments?: Array<{
        order?: string;
        shipped?: string;
        carrier?: string;
    }>;
}

const getSupplierOrderIds = async (
    orderId: string,
): Promise<Map<string, string>> => {
    try {
        const { data } = await $api.get<FetchSupplierOrderIdsResponse>(
            `/v1/orders/${orderId}/help/supplier-order-ids`,
        );
        const map = new Map<string, string>();
        data.data.forEach((entry) => {
            if (entry.supplierOrderIds?.[0]) {
                map.set(entry.supplierUid, entry.supplierOrderIds[0]);
            }
        });
        return map;
    } catch {
        return new Map();
    }
};

const getSupplierDetails = (
    vendor: Vendor,
    shipments: ShipmentSchema[],
    orderNumber: string,
    supplierOrderIdsMap: Map<string, string>,
): SupplierInfo => ({
    name: vendor.title,
    logo: vendor?.logo ?? '',
    sellerUid: vendor.externalUuid,
    accountNumber: vendor?.operationsAccountNumber ?? '',
    phone: vendor?.vendorPhone ?? '',
    email: vendor?.vendorEmail ?? '',
    primaryContact: vendor?.primaryContact ?? '',
    primaryPhone: vendor?.primaryContactPhone ?? '',
    primaryEmail: vendor?.primaryContactEmail ?? '',
    shipments: shipments?.length
        ? shipments.map((shipment) => ({
              order:
                  supplierOrderIdsMap.get(vendor.externalUuid) || orderNumber,
              shipped: shipment.trackingInfo?.createdAt,
              carrier: shipment.trackingInfo?.carrier,
          }))
        : [],
});

const getShipmentsForSupplier = (
    shipmentStatus: ShipmentSchema[] | null,
    sellerUid: string,
    proposalName: string,
): ShipmentSchema[] => {
    if (!Array.isArray(shipmentStatus)) return [];

    return shipmentStatus.filter(
        (shipment: ShipmentSchema) =>
            shipment.supplierUid === sellerUid ||
            shipment.seller === proposalName,
    );
};

const collectUniqueSuppliers = (
    selectedOrder: Order,
    vendorsMap: Record<string, Vendor>,
    shipmentStatus: ShipmentSchema[] | null,
    supplierOrderIdsMap: Map<string, string>,
): Map<string, SupplierInfo> => {
    const suppliersMap = new Map<string, SupplierInfo>();

    const proposals = selectedOrder.items.flatMap(
        (item: CatalogItemInOrder) => item.selectedProposals,
    );

    proposals.forEach((proposal: SelectedProposal) => {
        const sellerUid = proposal.sellerUid;

        if (suppliersMap.has(sellerUid)) return;

        const vendor = vendorsMap[sellerUid];
        const shipments = getShipmentsForSupplier(
            shipmentStatus,
            sellerUid,
            proposal.name,
        );

        const supplierDetails = getSupplierDetails(
            vendor,
            shipments,
            selectedOrder.orderNumber,
            supplierOrderIdsMap,
        );

        suppliersMap.set(sellerUid, supplierDetails);
    });

    return suppliersMap;
};

export const useSuppliers = (): SupplierInfo[] => {
    const selectedOrder = useSelector(getSelectedOrder);
    const vendorsMap = useSelector(getPropertyVendorsMap);
    const shipmentStatus = useSelector(getShipmentStatus);
    const [supplierOrderIdsMap, setSupplierOrderIdsMap] = useState<
        Map<string, string>
    >(new Map());

    useEffect(() => {
        if (!selectedOrder?.id) return;
        let cancelled = false;
        getSupplierOrderIds(selectedOrder.id).then((map) => {
            if (!cancelled) setSupplierOrderIdsMap(map);
        });
        return () => {
            cancelled = true;
        };
    }, [selectedOrder?.id]);

    return useMemo((): SupplierInfo[] => {
        if (!selectedOrder?.items || !vendorsMap) return [];

        const suppliersMap = collectUniqueSuppliers(
            selectedOrder,
            vendorsMap,
            shipmentStatus,
            supplierOrderIdsMap,
        );

        return Array.from(suppliersMap.values()).sort((a, b) =>
            a.name.localeCompare(b.name),
        );
    }, [selectedOrder, vendorsMap, shipmentStatus, supplierOrderIdsMap]);
};
